// client/src/components/CreditPointsForm.jsx
// The points-adjust form itself (no modal chrome), so the SAME mechanism can
// run inline inside the student card AND inside the bulk modal. A CREDIT/DEBIT
// toggle picks direction; a DEBIT never pushes anyone negative (the server
// skips students who can't cover it and reports how many were skipped).
//
// onApplied() fires only when the whole action went through (nothing skipped);
// on a partial debit the form stays put and shows what happened. onCancel()
// backs out.

import { useState } from 'react';
import { useCreditStudents } from '../hooks/useStudents.js';
import { useCamp } from '../hooks/useCamps.js';
import { currencySymbol } from '../lib/currency.js';
import { ApiError } from '../lib/api.js';

export default function CreditPointsForm({ campId, studentIds = [], onApplied, onCancel }) {
  const camp = useCamp(campId);
  const sym = currencySymbol(camp.data?.currency);
  const adjust = useCreditStudents(campId);

  const [direction, setDirection] = useState('CREDIT');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const n = studentIds.length;
  const isDebit = direction === 'DEBIT';
  // STAR camps take whole points only; SHEKEL/DOLLAR allow agorot (2 dp).
  // UX gate only — the server enforces the same rule (assertCurrencyAmount).
  const isStar = camp.data?.currency === 'STAR';
  const amt = isStar ? parseInt(amount, 10) : Math.round(parseFloat(amount) * 100) / 100;
  const valid = isStar
    ? Number.isInteger(amt) && amt >= 1
    : Number.isFinite(amt) && amt >= 0.01;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!valid) {
      setError(isStar ? 'יש להזין כמות נקודות שלמה וחיובית.' : 'יש להזין כמות נקודות חיובית.');
      return;
    }
    try {
      const r = await adjust.mutateAsync({
        studentIds,
        amount: amt,
        reason: reason.trim() || undefined,
        direction,
      });
      if (r.applied === 0) {
        setError(
          isDebit
            ? n > 1
              ? 'לאף תלמיד שנבחר אין יתרה מספקת לחיוב.'
              : 'לתלמיד אין יתרה מספקת לחיוב הזה.'
            : 'הפעולה לא בוצעה.'
        );
      } else if (r.skipped > 0) {
        setInfo(`בוצע ל-${r.applied} תלמידים · דולגו ${r.skipped} (אין יתרה מספקת).`);
        setAmount('');
      } else {
        onApplied?.();
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message || 'הפעולה נכשלה. נסו שוב.'
          : 'שגיאה לא צפויה. בדקו חיבור ונסו שוב.'
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        <DirButton active={!isDebit} tone="mint" label="הוספה (זיכוי)" onClick={() => setDirection('CREDIT')} />
        <DirButton active={isDebit} tone="clay" label="הורדה (חיוב)" onClick={() => setDirection('DEBIT')} />
      </div>

      {n > 1 && (
        <p className="text-sm text-ink-600">
          {isDebit ? 'הנקודות יורדו מ־' : 'הנקודות יתווספו ל־'}
          <strong className="num">{n}</strong> התלמידים שנבחרו.
        </p>
      )}

      <label className="block">
        <span className="label">כמות נקודות *</span>
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 end-3 grid place-items-center text-ink-400">
            {sym}
          </span>
          <input
            type="number"
            inputMode={isStar ? 'numeric' : 'decimal'}
            min={isStar ? '1' : '0.01'}
            step={isStar ? '1' : '0.01'}
            className="input pe-8 num"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            autoFocus
          />
        </div>
      </label>

      <label className="block">
        <span className="label">סיבה (אופציונלי)</span>
        <input
          type="text"
          maxLength={80}
          className="input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <span className="hint">יופיע בהיסטוריית התלמיד.</span>
      </label>

      {error && <div className="field-error">{error}</div>}
      {info && (
        <div className="rounded-xl bg-clay-50 border border-clay-200 text-clay-800 px-3 py-2 text-sm font-medium">
          {info}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          {info ? 'סגור' : 'ביטול'}
        </button>
        <button
          type="submit"
          className={isDebit ? 'btn-danger' : 'btn-success'}
          disabled={adjust.isPending || !valid}
        >
          {adjust.isPending
            ? 'מעדכן...'
            : isDebit
              ? n > 1 ? `הורד מ-${n}` : 'הורד נקודות'
              : n > 1 ? `הוסף ל-${n}` : 'הוסף נקודות'}
        </button>
      </div>
    </form>
  );
}

function DirButton({ active, tone, label, onClick }) {
  const activeCls =
    tone === 'mint'
      ? 'border-mint-500 bg-mint-50 text-mint-800'
      : 'border-clay-500 bg-clay-50 text-clay-800';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border-2 py-2.5 text-sm font-bold transition-all ${
        active ? activeCls : 'border-ink-200 text-ink-500 hover:border-ink-300'
      }`}
    >
      {label}
    </button>
  );
}
